import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  UploadSimple, ArrowRight, ArrowLeft, CheckCircle, XCircle,
  Warning, MagnifyingGlass, FileXls, FileJs, MapPin, ShieldCheck,
  Plugs, CaretDown
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { previewExcelImport, validateImport, importWithMapping, importCollectionFile } from '../services/api';

const STEPS = [
  { key: 'upload', label: 'Fichier', icon: UploadSimple },
  { key: 'mapping', label: 'Mapping', icon: Plugs },
  { key: 'validate', label: 'Validation', icon: ShieldCheck },
  { key: 'result', label: 'Resultat', icon: CheckCircle },
];

export default function ImportWizard({ collections, onComplete, onCancel }) {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [collection, setCollection] = useState('');
  const [mode, setMode] = useState('merge');
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const isExcel = file?.name?.endsWith('.xlsx') || file?.name?.endsWith('.xls');

  // Step 1 → Step 2: Preview file & get mapping suggestion
  const goToMapping = useCallback(async () => {
    if (!file || !collection) { toast.error('Selectionnez un module et un fichier'); return; }

    if (!isExcel) {
      // JSON files skip mapping, go directly to validate
      setStep(2);
      await runValidation();
      return;
    }

    setLoading(true);
    try {
      const res = await previewExcelImport(file, collection);
      setPreviewData(res.data);
      const suggested = res.data.suggested_mapping || {};
      setMapping(suggested);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lecture du fichier');
    }
    setLoading(false);
  }, [file, collection, isExcel]);

  // Step 2 → Step 3: Validate
  const runValidation = useCallback(async () => {
    if (!file || !collection) return;
    setLoading(true);
    try {
      const res = await validateImport(file, collection, mapping, skipDuplicates);
      setValidationResult(res.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation');
    }
    setLoading(false);
  }, [file, collection, mapping, skipDuplicates]);

  // Step 3 → Step 4: Import
  const runImport = useCallback(async () => {
    if (!file || !collection) return;
    setLoading(true);
    try {
      let res;
      if (isExcel && Object.keys(mapping).length > 0) {
        res = await importWithMapping(file, collection, mapping, mode, skipDuplicates);
      } else {
        res = await importCollectionFile(collection, file, mode);
      }
      setImportResult(res.data);
      setStep(3);
      toast.success(res.data.message || 'Import termine');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    }
    setLoading(false);
  }, [file, collection, mapping, mode, isExcel, skipDuplicates]);

  const updateMapping = (fileHeader, crmField) => {
    setMapping(prev => ({ ...prev, [fileHeader]: crmField }));
  };

  const colLabel = collections.find(c => c.key === collection)?.label || collection;

  return (
    <div className="space-y-5" data-testid="import-wizard">
      {/* Step Indicator */}
      <div className="flex items-center gap-1" data-testid="wizard-steps">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const isActive = idx === step;
          const isDone = idx < step;
          return (
            <React.Fragment key={s.key}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                isActive ? 'bg-[#2D5A4C] text-white' : isDone ? 'bg-emerald-100 text-emerald-700' : 'bg-[#F0EFEB] text-[#5E6D66]'
              }`} data-testid={`step-${s.key}`}>
                {isDone ? <CheckCircle size={14} weight="fill" /> : <Icon size={14} />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {idx < STEPS.length - 1 && <ArrowRight size={12} className="text-[#E8E6E1] flex-shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 0: Upload */}
      {step === 0 && (
        <Card className="border-[#E8E6E1]" data-testid="step-upload-content">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-[#1A2421]">Selectionner le fichier et le module</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#5E6D66] mb-1.5 block font-medium">Module cible</label>
                <Select value={collection} onValueChange={setCollection}>
                  <SelectTrigger className="text-xs" data-testid="wizard-collection-select"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                  <SelectContent>
                    {collections.map(c => <SelectItem key={c.key} value={c.key}>{c.label} ({c.count})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#5E6D66] mb-1.5 block font-medium">Mode d'import</label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="text-xs" data-testid="wizard-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merge">Fusion (ignorer existants)</SelectItem>
                    <SelectItem value="replace">Remplacer tout</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#5E6D66] mb-1.5 block font-medium">Fichier (.json, .xlsx)</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#E8E6E1] rounded-lg p-6 text-center cursor-pointer hover:border-[#2D5A4C] hover:bg-[#F9F8F5] transition-colors"
                data-testid="wizard-file-dropzone"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    {file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? <FileXls size={20} className="text-emerald-600" /> : <FileJs size={20} className="text-blue-600" />}
                    <span className="text-sm font-medium text-[#1A2421]">{file.name}</span>
                    <Badge variant="outline" className="text-[9px]">{(file.size / 1024).toFixed(1)} Ko</Badge>
                  </div>
                ) : (
                  <div>
                    <UploadSimple size={24} className="mx-auto text-[#5E6D66] mb-2" />
                    <p className="text-xs text-[#5E6D66]">Cliquez pour choisir un fichier</p>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept=".json,.xlsx,.xls" onChange={handleFileSelect} className="hidden" data-testid="wizard-file-input" />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={skipDuplicates} onCheckedChange={setSkipDuplicates} data-testid="wizard-skip-duplicates" />
              <div>
                <p className="text-xs font-medium text-[#1A2421]">Detection des doublons</p>
                <p className="text-[10px] text-[#5E6D66]">Ignore les doublons par email, SIREN, reference, etc.</p>
              </div>
            </div>

            {mode === 'replace' && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Warning size={16} className="text-red-500 mt-0.5 flex-shrink-0" weight="fill" />
                <p className="text-xs text-red-700">Le mode "Remplacer" supprimera toutes les donnees existantes du module avant l'import.</p>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={onCancel} data-testid="wizard-cancel-btn">Annuler</Button>
              <Button size="sm" onClick={goToMapping} disabled={!file || !collection || loading} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="wizard-next-btn">
                {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Suivant <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Mapping */}
      {step === 1 && previewData && (
        <Card className="border-[#E8E6E1]" data-testid="step-mapping-content">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <MapPin size={16} className="text-[#2D5A4C]" />
                Mapping des colonnes — {colLabel}
              </h3>
              <Badge className="bg-[#F0EFEB] text-[#5E6D66] text-[10px]">
                {previewData.sheets?.[0]?.row_count || 0} lignes detectees
              </Badge>
            </div>

            <p className="text-xs text-[#5E6D66]">Associez chaque colonne du fichier Excel au champ CRM correspondant. Les champs requis sont marques d'un asterisque.</p>

            <div className="border border-[#E8E6E1] rounded-lg overflow-hidden" data-testid="mapping-table">
              <div className="grid grid-cols-3 bg-[#F9F8F5] px-3 py-2 text-[10px] font-bold text-[#5E6D66] uppercase tracking-wider border-b border-[#E8E6E1]">
                <span>Colonne fichier</span>
                <span></span>
                <span>Champ CRM</span>
              </div>
              <div className="divide-y divide-[#F0EFEB] max-h-64 overflow-y-auto">
                {(previewData.sheets?.[0]?.headers || []).map((header, idx) => {
                  const currentMapping = mapping[header] || '';
                  const isRequired = previewData.required_fields?.includes(currentMapping);
                  return (
                    <div key={idx} className="grid grid-cols-3 items-center px-3 py-2 hover:bg-[#F9F8F5]" data-testid={`mapping-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] font-mono">{header}</Badge>
                      </div>
                      <div className="flex justify-center">
                        <ArrowRight size={14} className={currentMapping ? 'text-emerald-500' : 'text-[#E8E6E1]'} />
                      </div>
                      <Select value={currentMapping} onValueChange={v => updateMapping(header, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`mapping-select-${idx}`}>
                          <SelectValue placeholder="— Ignorer —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">— Ignorer —</SelectItem>
                          {(previewData.crm_fields || []).map(f => (
                            <SelectItem key={f.key} value={f.key}>
                              {f.label} {previewData.required_fields?.includes(f.key) ? '*' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sample data preview */}
            {previewData.sheets?.[0]?.sample_rows?.length > 0 && (
              <div>
                <p className="text-[10px] text-[#5E6D66] font-bold uppercase tracking-wider mb-1">Apercu des donnees (5 premieres lignes)</p>
                <div className="border border-[#E8E6E1] rounded-lg overflow-x-auto">
                  <table className="text-[10px] w-full">
                    <thead>
                      <tr className="bg-[#F9F8F5]">
                        {(previewData.sheets[0].headers || []).map((h, i) => (
                          <th key={i} className="px-2 py-1.5 text-left font-medium text-[#5E6D66] border-b border-[#E8E6E1] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.sheets[0].sample_rows.map((row, ri) => (
                        <tr key={ri} className="hover:bg-[#F9F8F5]">
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-2 py-1 text-[#1A2421] border-b border-[#F0EFEB] max-w-[120px] truncate whitespace-nowrap">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={() => setStep(0)} data-testid="wizard-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Retour
              </Button>
              <Button size="sm" onClick={runValidation} disabled={loading} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="wizard-validate-btn">
                {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ShieldCheck size={14} className="mr-1" />}
                Valider
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Validation */}
      {step === 2 && (
        <Card className="border-[#E8E6E1]" data-testid="step-validate-content">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#2D5A4C]" />
              Resultat de la validation — {colLabel}
            </h3>

            {validationResult ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-[#F9F8F5] border border-[#E8E6E1]">
                    <p className="text-[10px] text-[#5E6D66] uppercase">Total lignes</p>
                    <p className="text-lg font-bold text-[#1A2421]">{validationResult.total_rows}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-[10px] text-emerald-600 uppercase">Valides</p>
                    <p className="text-lg font-bold text-emerald-700">{validationResult.valid_rows}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-[10px] text-red-600 uppercase">Erreurs</p>
                    <p className="text-lg font-bold text-red-700">{validationResult.error_rows}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-[10px] text-amber-600 uppercase">Doublons</p>
                    <p className="text-lg font-bold text-amber-700">{validationResult.duplicate_count}</p>
                  </div>
                </div>

                {/* Errors */}
                {validationResult.errors?.length > 0 && (
                  <div data-testid="validation-errors">
                    <p className="text-xs font-semibold text-red-600 mb-1 flex items-center gap-1">
                      <XCircle size={12} weight="fill" /> Erreurs ({validationResult.errors.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100">
                      {validationResult.errors.slice(0, 20).map((err, idx) => (
                        <div key={idx} className="px-3 py-2 text-[10px]">
                          <span className="font-bold text-red-700">Ligne {err.row}:</span>
                          {err.errors.map((e, i) => <span key={i} className="text-red-600 ml-1">{e}</span>)}
                          <span className="text-[#5E6D66] ml-2">({Object.values(err.preview || {}).join(', ')})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {validationResult.warnings?.length > 0 && (
                  <div data-testid="validation-warnings">
                    <p className="text-xs font-semibold text-amber-600 mb-1 flex items-center gap-1">
                      <Warning size={12} weight="fill" /> Avertissements ({validationResult.warnings.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto border border-amber-200 rounded-lg divide-y divide-amber-100">
                      {validationResult.warnings.slice(0, 20).map((warn, idx) => (
                        <div key={idx} className="px-3 py-2 text-[10px]">
                          <span className="font-bold text-amber-700">Ligne {warn.row}:</span>
                          {warn.warnings.map((w, i) => <span key={i} className="text-amber-600 ml-1">{w}</span>)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-[#5E6D66] italic">{validationResult.summary}</p>
              </>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" />
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={() => setStep(isExcel ? 1 : 0)} data-testid="wizard-back-validate">
                <ArrowLeft size={14} className="mr-1" /> Retour
              </Button>
              <Button
                size="sm"
                onClick={runImport}
                disabled={loading || !validationResult || validationResult.valid_rows === 0}
                className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                data-testid="wizard-import-btn"
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UploadSimple size={14} className="mr-1" />}
                Importer {validationResult?.valid_rows || 0} ligne(s)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Result */}
      {step === 3 && importResult && (
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="step-result-content">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle size={24} className="text-emerald-600" weight="fill" />
              <h3 className="text-sm font-semibold text-emerald-800">{importResult.message}</h3>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-white border border-[#E8E6E1]">
                <p className="text-[10px] text-[#5E6D66] uppercase">Total</p>
                <p className="text-lg font-bold text-[#1A2421]">{importResult.total}</p>
              </div>
              <div className="p-3 rounded-lg bg-white border border-emerald-200">
                <p className="text-[10px] text-emerald-600 uppercase">Importes</p>
                <p className="text-lg font-bold text-emerald-700">{importResult.inserted}</p>
              </div>
              <div className="p-3 rounded-lg bg-white border border-[#E8E6E1]">
                <p className="text-[10px] text-[#5E6D66] uppercase">Ignores</p>
                <p className="text-lg font-bold text-[#5E6D66]">{importResult.skipped}</p>
              </div>
              <div className="p-3 rounded-lg bg-white border border-amber-200">
                <p className="text-[10px] text-amber-600 uppercase">Doublons</p>
                <p className="text-lg font-bold text-amber-700">{importResult.duplicates || 0}</p>
              </div>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs font-semibold text-red-600 mb-1">{importResult.errors.length} erreur(s)</p>
                {importResult.errors.slice(0, 5).map((e, i) => (
                  <p key={i} className="text-[10px] text-red-600">{e.id}: {e.error}</p>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button size="sm" onClick={onComplete} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="wizard-finish-btn">
                <CheckCircle size={14} className="mr-1" /> Terminer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
